import { PropsWithChildren, forwardRef, useImperativeHandle } from 'react';
import { IonCheckbox, IonInput, IonItem, IonItemGroup, IonList } from '@ionic/react';
import { Controller, useForm } from 'react-hook-form';
import PhoneNumberInput from '../../../../components-library/PhoneNumberInput';
import SignupIcon from '../../assets/icons/SignupIcon';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { StoreInterface } from '../../../../store/types';
import { REDUX_STORE_SLICES } from '../../../../services/exports/Constants';
import { SignupSchema, signupSchema } from './schema';
import { zodResolver } from '@hookform/resolvers/zod';
import useTheme from '../../../../hooks/ui/useTheme';

import classnames from 'classnames';
import Button from '../../components/buttons/Button';

type SignupFormProps = PropsWithChildren & {
  onSubmit: (data: SignupSchema) => void;
  hasSubmitBtn?: boolean;
  defaultValues?: SignupSchema;
};

export interface SignupFormHandle {
  submit: () => void;
}

const SignupForm = forwardRef<SignupFormHandle, SignupFormProps>(
  ({ hasSubmitBtn, defaultValues, onSubmit, children }, ref) => {
    const companyId = useSelector((state: StoreInterface) => state[REDUX_STORE_SLICES.INITIAL_DATA]?.company.id);
    const { i18n, t } = useTranslation(null, { keyPrefix: 'Modules:Mobile:Modals:SignUp:SignupForm' });
    const mergedDefaultValues: SignupSchema = {
      first_name: defaultValues?.first_name ?? '',
      last_name: defaultValues?.last_name ?? '',
      email: defaultValues?.email ?? '',
      phone_number: defaultValues?.phone_number ?? '',
      is_marketing_subscriber: defaultValues?.is_marketing_subscriber ?? false,
      offers_phone: false,
      lang: defaultValues?.lang ?? i18n.language,
      company_id: defaultValues?.company_id ?? companyId,
    };
    const form = useForm<SignupSchema>({
      defaultValues: mergedDefaultValues,
      resolver: zodResolver(signupSchema),
    });
    const theme = useTheme();

    useImperativeHandle(
      ref,
      () => ({
        submit: form.handleSubmit(onSubmit),
      }),
      [form.handleSubmit],
    );

    return (
      <form className="space-y-6">
        <IonList lines="none" className="space-y-1">
          <IonItemGroup className="flex">
            <IonItem className="flex-1">
              <Controller
                name="first_name"
                control={form.control}
                render={({ field, fieldState }) => (
                  <div>
                    <IonInput
                      className={classnames('bordered !px-4', {
                        'border-danger border-xs': !!fieldState.error?.message,
                      })}
                      style={{ '--placeholder-color': theme.color.mobile.tertiary }}
                      placeholder={t('placeholders.first_name')}
                      onIonChange={field.onChange}
                      {...field}
                    />
                    <p className="text-danger text-xs h-3">{fieldState.error?.message}</p>
                  </div>
                )}
              />
            </IonItem>
            <IonItem className="flex-1 [&::part(native)]:pl-0">
              <Controller
                name="last_name"
                control={form.control}
                render={({ field, fieldState }) => (
                  <div>
                    <IonInput
                      className={classnames('bordered !px-4', {
                        'border-danger border-xs': !!fieldState.error?.message,
                      })}
                      style={{ '--placeholder-color': theme.color.mobile.tertiary }}
                      placeholder={t('placeholders.last_name')}
                      onIonChange={field.onChange}
                      {...field}
                    />
                    <p className="text-danger text-xs h-3">{fieldState.error?.message}</p>
                  </div>
                )}
              />
            </IonItem>
          </IonItemGroup>
          <IonItem className="overflow-visible">
            <Controller
              name="email"
              control={form.control}
              render={({ field, fieldState }) => (
                <div className="w-full">
                  <IonInput
                    type="email"
                    className={classnames('bordered !px-4', { 'border-danger border-xs': !!fieldState.error?.message })}
                    style={{ '--placeholder-color': theme.color.mobile.tertiary }}
                    placeholder={t('placeholders.email')}
                    onIonChange={field.onChange}
                    {...field}
                  />
                  <p className="text-danger text-xs h-3">{fieldState.error?.message}</p>
                </div>
              )}
            />
          </IonItem>
          <IonItem>
            <Controller
              name="phone_number"
              control={form.control}
              render={({ field: { ref: _, ...rest }, fieldState }) => (
                <PhoneNumberInput
                  inputContainerStyle="border-xs bordered"
                  placeholder={t('placeholders.mobile_number')}
                  {...rest}
                  validationErrors={fieldState?.error?.message ? [fieldState.error?.message] : []}
                />
              )}
            />
          </IonItem>
        </IonList>
        <IonList lines="none" className="space-y-4">
          <IonItem
            className="[&::part(native)]:h-fit"
            style={{
              '--min-height': 'h-8',
            }}
          >
            <IonCheckbox
              labelPlacement="end"
              className="[&::part(container)]:rounded [&::part(container)]:border-mobile-tertiary [&::part(container)]:border-xs mr-sm"
              style={{
                '--checkbox-background-checked': theme.color.brand.primary,
                '--size': '32px',
              }}
            />
            <p className="text-xs text-gray-700 font-medium">{t('labels.exclusive_offers_via_email')}</p>
          </IonItem>
        </IonList>
        {children}
        {hasSubmitBtn && (
          <Button
            className="px-4 font-normal min-h-12 [&::part(native)]:rounded-lg [&::part(native)]:bg-brand-primary"
            expand="block"
            onClick={form.handleSubmit(onSubmit)}
            icon={<SignupIcon className="stroke-white" />}
            title={t('buttons.sign_up')}
          />
        )}
      </form>
    );
  },
);

export default SignupForm;
